import { Component, Prop, Element, Event, EventEmitter, Method, h } from '@stencil/core';

/**
 * Dialog are overlays that interrupt user interaction and demand a confirmation.
 */
@Component({
  tag: 'som-dialog',
  styleUrl: 'som-dialog.scss',
})
export class SomDialog {
  @Element() el: HTMLElement;

  /**
   * This property will be displayed in the header of your dialog.
   */
  @Prop() headline?: string;

  /**
   * This property sets the aria label of the dialog.
   */
   @Prop() dialogAriaLabel?: string;

  /**
   * This property will be used to further describe the dialog to users with screen readers. It won’t be displayed on the screen.
   */
  @Prop() description?: string;

  /**
   * This property is used to control the width of the dialog.
   */
  @Prop() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * This boolean property is used to enable closing the dialog with keyboard's `ESC` key.
   */
  @Prop() enableKeyboardClose: boolean = false;

  /**
   * This boolean property is used to enable closing the dialog when clicking on the overlay outside of the dialog.
   */
  @Prop() enableOverlayClose: boolean = false;

  /**
   * This property is used to customize the text of the confirm option in a confirmation dialog.
   */
  @Prop() confirmText: string = 'Confirm';

  /**
   * This property is used to customize the text of the cancel option in a confirmation dialog.
   */
  @Prop() cancelText: string = 'Cancel';

  /**
   * This boolean property is used to determine what color the confirm action button will be in a confirmation dialog.
   */
  @Prop() isDestructive: boolean = false;

  /**
   * This boolean property is used to hide the header's close button.
   */
   @Prop() hideCloseButton: boolean = true;

  /**
   * This event is emitted before the dialog opens.
   */
  @Event() dialogOpen: EventEmitter;

  /**
   * This event is emitted after the dialog closes.
   */
  @Event() dialogClose: EventEmitter;

  /**
   * This event is emitted when a confirmation button is clicked. The boolean `event.detail.confirmation` is returned, where confirm is equal to `true` and cancel is equal to `false`.
   * @Returns `event.detail` will contain the confirmation status in as a `boolean` value where confirm is equal to `true` and cancel is equal to `false`.
   */
  @Event() dialogConfirm: EventEmitter;

  /**
   * Private Variables
   */
  modal;

  /**
   * This method will open the dialog.
   * @returns Promise will resolve when the open animation ends.
   */
  @Method()
  open(): Promise<void> {
    return this.modal.open();
  }

  /**
   * This method will close the dialog.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  close(): Promise<void> {
    return this.modal.close();
  }

  /**
   * This method will refresh the dialog. This is useful when dialog data is loaded asynchronously, which allows the component to find elements to focus on and provide accessibility support.
   * @returns Promise will resolve after the refresh.
   */
  @Method()
  refresh(): Promise<void> {
    return this.modal.refresh();
  }

  confirm(confirmation: boolean) {
    this.dialogConfirm.emit({ confirmation });
    this.close();
  }

  handleOpen(e) {
    // Stop event here and re-emit event from this component
    e.stopPropagation();
    this.dialogOpen.emit();
  }

  handleClose(e) {
    // Stop event here and re-emit event from this component
    e.stopPropagation();
    this.dialogClose.emit();
  }

  componentDidLoad() {
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);

    this.modal = this.el.querySelector('som-modal');
    this.modal.addEventListener('modalOpen', this.handleOpen);
    this.modal.addEventListener('modalClose', this.handleClose);
  }

  disconnectedCallback() {
    this.modal.removeEventListener('modalOpen', this.handleOpen);
    this.modal.removeEventListener('modalClose', this.handleClose);
  }

  render() {
    return (
      <som-modal
        headline={this.headline}
        aria-label={this.dialogAriaLabel || null}
        description={this.description}
        size={this.size}
        enableKeyboardClose={this.enableKeyboardClose}
        enableOverlayClose={this.enableOverlayClose}
        hideCloseButton={this.hideCloseButton}
      >
        <slot />
        <div class="modal-confirmation-actions d-flex flex-column-reverse flex-md-row justify-content-md-end mt-3">
          <som-button size="block" class="mr-md-0_5 ml-md-1" onClick={() => this.confirm(false)}>{this.cancelText}</som-button>
          <som-button size="block" class="mb-1 mb-md-0 mr-md-0_5 ml-md-1" color={this.isDestructive ? 'danger' : 'primary'} onClick={() => this.confirm(true)}>
            {this.confirmText}
          </som-button>
        </div>
      </som-modal>
    );
  }
}
